import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import moment from "moment";
import { getOrderDetails, payOrder } from "../Redux/Actions/OrderActions";
import { fetchSavedCard } from "../Redux/Actions/savedCardActions";
import { ORDER_PAY_RESET } from "../Redux/Constants/OrderConstants";
import Header from "./../components/Header";
import Loading from "./../components/LoadingError/Loading";
import Message from "./../components/LoadingError/Error";
import SquarePaymentProcessor from "./SquarePaymentProccesor.js";
import { appId, locationId } from "./Ids.js";
import "../App.css";
import "./payment.css";

const OrderScreen = () => {
  const { id: orderId } = useParams();
  const dispatch        = useDispatch();

  const orderDetails = useSelector((state) => state.orderDetails);
  const { order, loading, error } = orderDetails;

  const orderPay = useSelector((state) => state.orderPay);
  const { loading: loadingPay, success: successPay } = orderPay;

  const { savedCard, loading: savedCardLoading } = useSelector(
    (state) => state.savedCard
  );

  useEffect(() => {
    if (!order || successPay || order._id !== orderId) {
      dispatch({ type: ORDER_PAY_RESET });
      dispatch(getOrderDetails(orderId));
    }
  }, [dispatch, orderId, order, successPay]);

  const { userInfo } = useSelector((state) => state.userLogin);
  useEffect(() => {
    if (userInfo) dispatch(fetchSavedCard());
  }, [dispatch, userInfo]);

  const handlePaymentSuccess = (paymentResult) => {
    dispatch(payOrder(orderId, paymentResult));
    // NOTE: do NOT clear the guestOrderToken here.
    // After payOrder succeeds, successPay triggers a getOrderDetails refetch
    // which still needs the token to authorize the GET /:id request.
    // The token expires naturally after 24h — plenty of time to view the order.
  };

  const handlePaymentError = (error) => {
    console.error("Payment error:", error);
  };

  const isGuestOrder = order && !order.user;
  const guestEmail   = order?.guestEmail;
  const showSuggest  = isGuestOrder && order?.suggestAccountCreation === true;

  return (
    <>
      <Header />

      <div className="container">
        {loading ? (
          <Loading />
        ) : error ? (
          <Message variant="alert-danger">{error}</Message>
        ) : (
          <>
            {showSuggest && (
              <div
                className="alert alert-info d-flex align-items-center justify-content-between mt-3"
                style={{ fontSize: 13, borderRadius: 8 }}
              >
                <span>
                  <strong>Want to track future orders?</strong>{" "}
                  Create a free account with{" "}
                  <strong>{guestEmail}</strong> and this order will be linked automatically.
                </span>
                <Link
                  to={`/register?redirect=profile&email=${encodeURIComponent(guestEmail)}`}
                  className="btn btn-sm btn-outline-primary ms-3"
                  style={{ whiteSpace: "nowrap" }}
                >
                  Create Account
                </Link>
              </div>
            )}

            <div className="row order-detail">

              {/* Customer Information */}
              <div className="col-lg-4 col-sm-4 mb-lg-4 mb-5 mb-sm-0">
                <div className="row">
                  <div className="col-md-4 center">
                    <div className="alert-success order-box">
                      <i className="fas fa-user"></i>
                    </div>
                  </div>
                  <div className="col-md-8 center">
                    <h5><strong>Customer</strong></h5>
                    <p>{order.user?.name || "Guest"}</p>
                    <p>
                      <a href={`mailto:${order.user?.email || guestEmail}`}>
                        {order.user?.email || guestEmail}
                      </a>
                    </p>
                    {isGuestOrder && (
                      <span style={{ fontSize: 11, color: "#888", fontStyle: "italic" }}>
                        Guest order
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Order Information */}
              <div className="col-lg-4 col-sm-4 mb-lg-4 mb-5 mb-sm-0">
                <div className="row">
                  <div className="col-md-4 center">
                    <div className="alert-success order-box">
                      <i className="fas fa-truck-moving"></i>
                    </div>
                  </div>
                  <div className="col-md-8 center">
                    <h5><strong>Order info</strong></h5>
                    <p>Shipping: {order.shippingAddress.country}</p>
                    <p>Pay method: {order.paymentMethod}</p>
                    {order.isPaid ? (
                      <div className="bg-info p-2 col-12">
                        <p className="text-white text-center text-sm-start">
                          Paid on {moment(order.paidAt).calendar()}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-danger p-2 col-12">
                        <p className="text-white text-center text-sm-start">Not Paid</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Delivery Information */}
              <div className="col-lg-4 col-sm-4 mb-lg-4 mb-5 mb-sm-0">
                <div className="row">
                  <div className="col-md-4 center">
                    <div className="alert-success order-box">
                      <i className="fas fa-map-marker-alt"></i>
                    </div>
                  </div>
                  <div className="col-md-8 center">
                    <h5><strong>Deliver to</strong></h5>
                    <p>
                      Address: {order.shippingAddress.city},{" "}
                      {order.shippingAddress.state},{" "}
                      {order.shippingAddress.street1},{" "}
                      {order.shippingAddress.zipcode}
                    </p>
                    {order.isDelivered ? (
                      <div className="bg-info p-2 col-12">
                        <p className="text-white text-center text-sm-start">
                          Delivered on {moment(order.deliveredAt).calendar()}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-danger p-2 col-12">
                        <p className="text-white text-center text-sm-start">Not Delivered</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="row order-products justify-content-between">

              {/* Order Items */}
              <div className="col-lg-8">
                {order.orderItems.length === 0 ? (
                  <Message variant="alert-info mt-5">Your order is empty</Message>
                ) : (
                  order.orderItems.map((item, index) => (
                    <div className="order-product row" key={index}>
                      <div className="col-md-3 col-6">
                        <img src={item.image} alt={item.name} />
                      </div>
                      <div className="col-md-5 col-6 d-flex align-items-center">
                        <Link to={`/products/${item.product}`}>
                          <h6>{item.name}</h6>
                        </Link>
                      </div>
                      <div className="mt-3 mt-md-0 col-md-2 col-6 d-flex align-items-center flex-column justify-content-center">
                        <h4>QUANTITY</h4>
                        <h6>{item.qty}</h6>
                      </div>
                      <div className="mt-3 mt-md-0 col-md-2 col-6 align-items-end d-flex flex-column justify-content-center">
                        <h4>SUBTOTAL</h4>
                        <h6>${(item.qty * item.price).toFixed(2)}</h6>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Order Summary + Payment */}
              <div className="col-lg-3 d-flex align-items-end flex-column mt-5 subtotal-order">
                <table className="table table-bordered">
                  <tbody>
                    <tr>
                      <td><strong>Products</strong></td>
                      <td>${order.itemsPrice}</td>
                    </tr>
                    <tr>
                      <td><strong>Shipping</strong></td>
                      <td>${order.shippingPrice}</td>
                    </tr>
                    <tr>
                      <td><strong>Tax</strong></td>
                      <td>${order.taxPrice}</td>
                    </tr>
                    {order.discountAmount > 0 && (
                      <tr style={{ color: "#198754" }}>
                        <td>
                          <strong>Discount</strong>{" "}
                          {order.discountCode && (
                            <span style={{
                              fontSize:     11,
                              background:   "#d1fae5",
                              border:       "1px solid #6ee7b7",
                              borderRadius: 4,
                              padding:      "1px 6px",
                              fontFamily:   "monospace",
                              fontWeight:   700,
                            }}>
                              {order.discountCode}
                            </span>
                          )}
                        </td>
                        <td>−${order.discountAmount.toFixed(2)}</td>
                      </tr>
                    )}
                    <tr>
                      <td><strong>Total</strong></td>
                      <td>${order.totalPrice}</td>
                    </tr>
                  </tbody>
                </table>

                {!order.isPaid && (
                  <div className="col-12">
                    {loadingPay || savedCardLoading ? (
                      <Loading />
                    ) : (
                      <div className="payment-wrapper">
                        {savedCard && !isGuestOrder && (
                          <div className="order-saved-card-notice">
                            <i className="fas fa-shield-alt"></i>
                            <span>
                              Paying with saved {savedCard.cardBrand} •••• {savedCard.last4}
                            </span>
                          </div>
                        )}
                        <SquarePaymentProcessor
                          appId={appId}
                          locationId={locationId}
                          paymentMethod={order.paymentMethod}
                          order={order}
                          onPaymentSuccess={handlePaymentSuccess}
                          onPaymentError={handlePaymentError}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default OrderScreen;